// coffee-gear.js — what the user actually brews and grinds with.
//
// This is the seam that was missing. The appliances table has always known
// whether someone owns a Fellow Aiden or an AeroPress, but nothing in the
// coffee stack read it: coffee.js, aiden.js, brew-helpers.js and the Morning*
// components all assumed Aiden + Ode unconditionally. Owning an AeroPress had
// literally no effect on any brew number.
//
// Everything here derives from APPLIANCE_LIBRARY capabilities rather than
// hardcoded ids where possible, so adding a new brewer to the library picks it
// up without touching this file.

import { APPLIANCE_LIBRARY } from "./appliances";

// Grind scales differ per grinder, and a number means nothing without one.
// "5.4" is mid-range on an Ode and impossibly fine on a Baratza Encore.
export const GRIND_SCALES = {
  ode_gen2: {
    id: "ode_gen2",
    label: "Fellow Ode Gen 2",
    min: 1, max: 11, step: 0.1,
    // The canonical bands from docs/coffee-handoff.md.
    hot: 5.4, cold: 8.2,
    unit: "setting",
  },
  // A generic burr grinder with numbered detents. 1–40 covers Baratza Encore
  // and most of the affordable-burr category; the hot/cold anchors are the
  // usual medium and coarse marks on that range.
  generic_numeric: {
    id: "generic_numeric",
    label: "Numbered grinder",
    min: 1, max: 40, step: 1,
    hot: 18, cold: 30,
    unit: "setting",
  },
  // Hand grinders count clicks from zero. Comandante-ish range.
  clicks: {
    id: "clicks",
    label: "Clicks from zero",
    min: 1, max: 50, step: 1,
    hot: 22, cold: 34,
    unit: "clicks",
  },
  // Nothing numeric to report — the UI shows a descriptor ("medium-fine")
  // instead of a number it cannot honestly produce.
  descriptive: {
    id: "descriptive",
    label: "No numbered scale",
    min: null, max: null, step: null,
    hot: null, cold: null,
    unit: null,
  },
};

export const DEFAULT_SCALE = GRIND_SCALES.generic_numeric;

// Brew methods, coarsest-grained first. `profileShape` says whether the Fellow
// pulse/bloom profile is meaningful for this method — it is not for immersion
// or espresso, where there are no pulses to schedule.
const METHODS = {
  drip_brew: { id: "drip", label: "Drip brewer", profileShape: "pulsed" },
  pour_over: { id: "pour_over", label: "Pour over", profileShape: "pulsed" },
  aeropress: { id: "aeropress", label: "AeroPress", profileShape: "immersion" },
  french_press: { id: "french_press", label: "French press", profileShape: "immersion" },
  espresso_pull: { id: "espresso", label: "Espresso", profileShape: "espresso" },
};

// Priority when someone owns several.
//
// Ordered by SPECIFICITY, not by preference, because a capability list says what
// a device *can do*, not what it *is*. The AeroPress declares pour_over,
// french_press AND aeropress — it genuinely does all three — so checking the
// generic pour_over first would label an AeroPress owner's setup "Pour over"
// and hand them pour-over guidance for an immersion brewer.
//
// Within that: a drip brewer is the daily driver if you own one, and espresso
// is a deliberate occasion rather than a default, so it sorts last.
const METHOD_PRIORITY = ["drip_brew", "aeropress", "french_press", "pour_over", "espresso_pull"];

function ownedLibraryIds(appliances) {
  return new Set((appliances || []).map(a => a.library_id).filter(Boolean));
}

function capabilitiesOf(ownedIds) {
  const caps = new Set();
  for (const item of APPLIANCE_LIBRARY) {
    if (ownedIds.has(item.id)) item.capabilities.forEach(c => caps.add(c));
  }
  return caps;
}

/**
 * Resolve a user's coffee setup from their appliances.
 *
 * Returns:
 *   hasAiden    — Fellow Aiden specifically. Gates profile push and the pulse UI.
 *   hasOde      — Fellow Ode Gen 2 specifically. Gates the 1–11 decimal scale.
 *   hasAnyGear  — false means don't show brew guidance at all.
 *   method      — { id, label, profileShape }
 *   grinder     — a GRIND_SCALES entry
 *
 * `preferences.grind_scale` overrides the inferred scale, for someone whose
 * grinder is not in the library.
 */
export function coffeeGear(appliances, preferences) {
  const ownedIds = ownedLibraryIds(appliances);
  const caps = capabilitiesOf(ownedIds);

  const hasAiden = ownedIds.has("fellow_aiden");
  const hasOde = ownedIds.has("ode_gen2");

  // Method: first owned capability in priority order.
  let method = null;
  for (const cap of METHOD_PRIORITY) {
    if (caps.has(cap)) { method = METHODS[cap]; break; }
  }

  // Grinder: the Ode has a known scale; otherwise honour an explicit override,
  // then fall back to a generic numeric scale for any burr grinder, then to
  // descriptive-only when we know of no grinder at all.
  const override = preferences?.grind_scale;
  let grinder;
  if (hasOde) {
    grinder = GRIND_SCALES.ode_gen2;
  } else if (override && GRIND_SCALES[override]) {
    grinder = GRIND_SCALES[override];
  } else if (caps.has("burr_grind")) {
    grinder = GRIND_SCALES.generic_numeric;
  } else {
    grinder = GRIND_SCALES.descriptive;
  }

  return {
    hasAiden,
    hasOde,
    hasAnyGear: ownedIds.size > 0 && !!method,
    method: method || METHODS.pour_over,   // sane default for guidance
    methodKnown: !!method,
    grinder,
  };
}

/**
 * Can we show a Fellow profile (bloom + pulse schedule) for this setup?
 * Only meaningful on an Aiden — everyone else gets dose/ratio/temp/grind.
 */
export function showsAidenProfile(gear) {
  return !!gear?.hasAiden;
}

/** Rescale a grind setting from one grinder's range onto another's. */
export function rescaleGrind(value, from, to) {
  if (value == null || !from?.max || !to?.max) return null;
  const ratio = (value - from.min) / (from.max - from.min);
  const mapped = to.min + ratio * (to.max - to.min);
  const step = to.step || 1;
  return +(Math.round(mapped / step) * step).toFixed(step < 1 ? 1 : 0);
}
